(() => {
  if (window.__rpdUrlParamsLoaded) return;
  window.__rpdUrlParamsLoaded = true;

  const config = window.rpdUrlParamsConfig;
  if (!config || !config.enabled) return;

  const KEYS = Array.isArray(config.keys)
    ? config.keys
    : ['rpd_p1', 'rpd_p2', 'rpd_p3', 'rpd_p4', 'rpd_p5'];

  window.rpdUrlParams = window.rpdUrlParams || {};
  window.rpdUrlParams.keys = KEYS;
  window.rpdUrlParams.config = config;

  const context = window.rpdPageContext || {};
  const pageType = (() => {
    if (context.type) return context.type;
    if (document.body.classList.contains('template-product')) return 'product';
    if (document.body.classList.contains('template-collection')) return 'collection';
    return null;
  })();

  const readRpdParamsFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const current = {};
    KEYS.forEach((key) => {
      if (!params.has(key)) return;
      const value = params.get(key);
      if (value != null && value !== '') {
        current[key] = value;
      }
    });
    return current;
  };

  const applyCurrentParamsToDocument = (current) => {
    KEYS.forEach((key) => {
      document.documentElement.removeAttribute(`data-${key}`);
    });
    document.documentElement.removeAttribute('data-rpd-params');
    document.documentElement.removeAttribute('data-rpd-auto');
    if (!current) return;
    Object.keys(current).forEach((key) => {
      document.documentElement.setAttribute(`data-${key}`, current[key]);
    });
    if (Object.keys(current).length) {
      document.documentElement.setAttribute('data-rpd-params', 'true');
    }
    if (config.autoKey) {
      const params = new URLSearchParams(window.location.search);
      if (params.get(config.autoKey) === String(config.autoValue)) {
        document.documentElement.setAttribute('data-rpd-auto', 'true');
      }
    }
  };

  const normalizeValue = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value).trim();
    return str;
  };

  const resolveCustomValue = (rawValue, metaMap) => {
    const trimmed = normalizeValue(rawValue);
    if (!trimmed) return '';
    const match = trimmed.match(/^\{\s*([^}]+)\s*\}$/);
    if (match) {
      const key = match[1];
      if (metaMap && Object.prototype.hasOwnProperty.call(metaMap, key)) {
        const metaValue = metaMap[key];
        if (metaValue === null || metaValue === undefined) return '';
        if (Array.isArray(metaValue)) return metaValue.map((item) => String(item)).join(',');
        return String(metaValue);
      }
      return '';
    }
    return trimmed;
  };

  const getAllowedCollections = () => {
    const allowed = window.rpdUrlParams?.allowedCollections;
    if (!Array.isArray(allowed) || !allowed.length) return null;
    return allowed
      .map((handle) => String(handle).trim().toLowerCase())
      .filter((handle) => handle);
  };

  const filterCollections = (collections) => {
    const allowed = getAllowedCollections();
    if (!allowed) return collections || [];
    const allowedSet = new Set(allowed);
    return (collections || []).filter((handle) => allowedSet.has(String(handle).toLowerCase()));
  };

  const getProductValue = (source, customValue, product) => {
    if (!product || !source || source === 'none') return '';
    const variant = product.variant || {};
    const filteredCollections = filterCollections(product.collections);
    switch (source) {
      case 'product_handle':
        return normalizeValue(product.handle);
      case 'product_title':
        return normalizeValue(product.title);
      case 'product_type':
        return normalizeValue(product.type);
      case 'product_vendor':
        return normalizeValue(product.vendor);
      case 'variant_id':
        return normalizeValue(variant.id);
      case 'variant_sku':
        return normalizeValue(variant.sku);
      case 'variant_price':
        return normalizeValue(variant.price);
      case 'variant_option1':
        return normalizeValue(variant.option1);
      case 'variant_option2':
        return normalizeValue(variant.option2);
      case 'variant_option3':
        return normalizeValue(variant.option3);
      case 'first_collection_handle':
        return normalizeValue(filteredCollections[0]);
      case 'all_collection_handles':
        return normalizeValue(filteredCollections.join(','));
      case 'static':
        return resolveCustomValue(customValue, product.metafields);
      default:
        return '';
    }
  };

  const getCollectionValue = (source, customValue, collection) => {
    if (!collection || !source || source === 'none') return '';
    switch (source) {
      case 'collection_handle':
        return normalizeValue(collection.handle);
      case 'collection_title':
        return normalizeValue(collection.title);
      case 'collection_products_count':
        return normalizeValue(collection.productsCount);
      case 'static':
        return resolveCustomValue(customValue, collection.metafields);
      default:
        return '';
    }
  };

  const buildParams = (existingParams = readRpdParamsFromUrl()) => {
    const params = {};
    const alwaysPass = Array.isArray(config.alwaysPass) ? config.alwaysPass : [];
    if (pageType === 'product') {
      const product = context.product;
      const sources = config.product?.sources || [];
      const values = config.product?.values || [];
      KEYS.forEach((key, index) => {
        const source = sources[index] || 'none';
        if (source === 'current_param') {
          const value = normalizeValue(existingParams[key]);
          if (value) params[key] = value;
          return;
        }
        if (source === 'none' && alwaysPass.includes(key)) {
          const value = normalizeValue(existingParams[key]);
          if (value) params[key] = value;
          return;
        }
        const value = getProductValue(source, values[index], product);
        if (value) params[key] = value;
      });
    } else if (pageType === 'collection') {
      const collection = context.collection;
      const sources = config.collection?.sources || [];
      const values = config.collection?.values || [];
      KEYS.forEach((key, index) => {
        const source = sources[index] || 'none';
        if (source === 'current_param') {
          const value = normalizeValue(existingParams[key]);
          if (value) params[key] = value;
          return;
        }
        if (source === 'none' && alwaysPass.includes(key)) {
          const value = normalizeValue(existingParams[key]);
          if (value) params[key] = value;
          return;
        }
        const value = getCollectionValue(source, values[index], collection);
        if (value) params[key] = value;
      });
    }
    return params;
  };

  const mergeParams = (...sources) => {
    const merged = {};
    sources.forEach((source) => {
      if (!source) return;
      KEYS.forEach((key) => {
        const value = normalizeValue(source[key]);
        if (value) merged[key] = value;
      });
    });
    return merged;
  };

  const refreshCurrentParams = () => {
    const rawCurrent = readRpdParamsFromUrl();
    const effectiveCurrent = mergeParams(buildParams(rawCurrent), rawCurrent);

    window.rpdUrlParams.raw = rawCurrent;
    window.rpdUrlParams.url = rawCurrent;
    window.rpdUrlParams.current = effectiveCurrent;
    window.rpdUrlParams.effective = effectiveCurrent;
    window.rpdUrlParamsCurrent = effectiveCurrent;
    applyCurrentParamsToDocument(effectiveCurrent);
    document.dispatchEvent(
      new CustomEvent('rpd:url-params:updated', {
        detail: {
          raw: rawCurrent,
          current: effectiveCurrent,
        },
      })
    );

    return effectiveCurrent;
  };

  window.rpdUrlParams.buildParams = buildParams;
  window.rpdUrlParams.refreshCurrent = refreshCurrentParams;

  const getMappedFilters = (params) => {
    if (pageType !== 'collection') return [];
    const filterKeys = config.collection?.filterKeys || [];
    const mapped = [];
    KEYS.forEach((key, index) => {
      const filterKey = normalizeValue(filterKeys[index]);
      const value = params.get(key);
      if (!filterKey || !value) return;
      mapped.push({ filterKey, value });
    });
    return mapped;
  };

  const mappedFiltersMatch = (params, mapped) => {
    if (!mapped.length) return false;
    return mapped.every(({ filterKey, value }) => params.get(filterKey) === value);
  };

  const applyCollectionFilters = () => {
    if (pageType !== 'collection') return false;
    const params = new URLSearchParams(window.location.search);
    const autoActive = config.autoKey && params.get(config.autoKey) === String(config.autoValue);
    if (!autoActive) return false;
    const hasRpdParam = KEYS.some((key) => params.has(key));
    if (!hasRpdParam) return false;

    const mapped = getMappedFilters(params);
    if (!mapped.length) return false;

    let changed = false;
    mapped.forEach(({ filterKey, value }) => {
      if (!params.has(filterKey)) {
        params.set(filterKey, value);
        changed = true;
      }
    });

    if (!changed) return false;
    params.delete('page');
    const nextUrl = `${window.location.pathname}?${params.toString()}`;
    if (nextUrl !== window.location.href) {
      window.location.replace(nextUrl);
      return true;
    }
    return false;
  };

  const shouldSkipLink = (link) => {
    if (!link || !link.getAttribute) return true;
    if (link.getAttribute('data-rpd-params') === 'off') return true;
    if (link.getAttribute('data-rpd-params') === 'false') return true;
    if (link.hasAttribute('data-no-rpd-params')) return true;
    const href = link.getAttribute('href');
    if (!href) return true;
    const lowered = href.toLowerCase();
    if (lowered.startsWith('#') || lowered.startsWith('mailto:') || lowered.startsWith('tel:')) return true;
    if (lowered.startsWith('javascript:')) return true;
    return false;
  };

  const shouldSkipUrl = (url) => {
    if (!url || url.origin !== window.location.origin) return true;
    if (
      url.pathname.startsWith('/cart') ||
      url.pathname.startsWith('/checkout') ||
      url.pathname.startsWith('/account') ||
      url.pathname.startsWith('/admin')
    ) {
      return true;
    }
    return false;
  };

  const appendParamsToLinks = (root = document) => {
    if (!pageType) return;
    const params = buildParams();
    const hasParams = Object.keys(params).length > 0;
    if (!hasParams) return;

    const links = root.querySelectorAll('a[href]');
    links.forEach((link) => {
      if (shouldSkipLink(link)) return;
      let url;
      try {
        url = new URL(link.getAttribute('href'), window.location.origin);
      } catch (e) {
        return;
      }
      if (shouldSkipUrl(url)) return;

      const keepExisting = link.getAttribute('data-rpd-params') === 'keep';
      KEYS.forEach((key) => {
        if (!params[key]) return;
        if (keepExisting && url.searchParams.has(key)) return;
        url.searchParams.set(key, params[key]);
      });

      if (config.autoKey && config.autoValue && !url.searchParams.has(config.autoKey)) {
        url.searchParams.set(config.autoKey, String(config.autoValue));
      }

      const nextHref = `${url.pathname}${url.search}${url.hash}`;
      if (link.getAttribute('href') !== nextHref) {
        link.setAttribute('href', nextHref);
      }
    });
  };

  const buildClearFiltersUrl = () => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const keysToRemove = new Set(KEYS);
    const toDelete = [];
    params.forEach((value, key) => {
      if (keysToRemove.has(key)) toDelete.push(key);
      if (config.autoKey && key === config.autoKey) toDelete.push(key);
      if (key === 'page') toDelete.push(key);
      if (key.startsWith('filter.')) toDelete.push(key);
    });
    toDelete.forEach((key) => params.delete(key));
    const nextUrl = `${url.pathname}${params.toString() ? `?${params.toString()}` : ''}${url.hash}`;
    return nextUrl;
  };

  const getCollectionMeta = () => {
    const meta = document.querySelector('[data-rpd-collection-grid]');
    if (!meta) return null;
    return {
      element: meta,
      filteredCount: Number(meta.dataset.filteredCount || 0),
      totalCount: Number(meta.dataset.totalCount || 0),
      currentPage: Number(meta.dataset.currentPage || 1),
      totalPages: Number(meta.dataset.totalPages || 1),
      productsPerPage: Number(meta.dataset.productsPerPage || 0),
      hasFilters: meta.dataset.hasFilters === 'true',
    };
  };

  const ensureViewAllButton = (container, url) => {
    if (!container || !url) return;
    if (container.querySelector('[data-rpd-view-all]')) return;
    const wrapper = document.createElement('div');
    wrapper.className = 'center rpd-view-all';
    const button = document.createElement('a');
    button.href = url;
    button.className = 'button button--primary';
    button.textContent = config.viewAllLabel || 'View all';
    button.setAttribute('data-rpd-view-all', 'true');
    wrapper.appendChild(button);
    container.appendChild(wrapper);
  };

  const maybeHandleCollectionAuto = () => {
    if (pageType !== 'collection') return;
    const meta = getCollectionMeta();
    if (!meta) return;
    const params = new URLSearchParams(window.location.search);
    const autoActive =
      config.autoKey && params.get(config.autoKey) === String(config.autoValue);
    const hasRpdParam = KEYS.some((key) => params.has(key));
    if (!autoActive || !hasRpdParam || !meta.hasFilters) return;

    const mapped = getMappedFilters(params);
    if (!mappedFiltersMatch(params, mapped)) return;

    const threshold = Number(config.autoRemoveThreshold || 0);
    if (threshold > 0 && meta.filteredCount < threshold) {
      const nextUrl = buildClearFiltersUrl();
      if (nextUrl !== window.location.href) {
        window.location.replace(nextUrl);
      }
      return;
    }

    if (meta.totalCount <= meta.filteredCount) return;
    if (meta.currentPage < meta.totalPages) return;

    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const pagination = container.querySelector('.pagination-wrapper') || container;
    const nextUrl = buildClearFiltersUrl();
    ensureViewAllButton(pagination, nextUrl);
  };

  const updateVariantContext = (variant) => {
    if (!context.product || !variant) return;
    context.product.variant = {
      id: variant.id,
      sku: variant.sku,
      price: variant.price,
      option1: variant.option1,
      option2: variant.option2,
      option3: variant.option3,
    };
  };

  const hasVariantBasedProductParams = () => {
    if (pageType !== 'product') return false;
    const sources = Array.isArray(config.product?.sources) ? config.product.sources : [];
    return sources.some((source) => typeof source === 'string' && source.startsWith('variant_'));
  };

  const init = () => {
    refreshCurrentParams();
    appendParamsToLinks();
    if (!applyCollectionFilters()) {
      maybeHandleCollectionAuto();
    }

    document.addEventListener('shopify:section:load', (event) => {
      refreshCurrentParams();
      if (event?.target) appendParamsToLinks(event.target);
      if (!applyCollectionFilters()) {
        maybeHandleCollectionAuto();
      }
    });

    if (
      hasVariantBasedProductParams() &&
      typeof subscribe === 'function' &&
      window.PUB_SUB_EVENTS?.variantChange
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!data || !data.variant) return;
        updateVariantContext(data.variant);
        refreshCurrentParams();
        appendParamsToLinks();
      });
    }

    if (pageType === 'collection') {
      const gridContainer = document.getElementById('ProductGridContainer');
      if (gridContainer) {
        const observer = new MutationObserver(() => {
          appendParamsToLinks(gridContainer);
          if (!applyCollectionFilters()) {
            maybeHandleCollectionAuto();
          }
        });
        observer.observe(gridContainer, { childList: true, subtree: true });
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
